/**
 * The moment after the first build — a full-screen receipt before the studio.
 *
 * The overlay used to vanish straight into the editor, so the two minutes of work ended with no
 * beat: no "here is what you got", no sense of what it cost. This is that beat. Column one says
 * what was built in the learner's terms; column two shows the run itself (tokens, duration, steps),
 * which is also the first place cost becomes a visible, normal thing rather than a mystery.
 */

const fmt = (n: number): string => n.toLocaleString('en-US');

const fmtDuration = (s: number): string => {
  const m = Math.floor(s / 60);
  return m > 0 ? `${m}m ${s % 60}s` : `${s}s`;
};

export interface BuildSummaryData {
  usage?: { inputTokens: number; outputTokens: number };
  durationS: number;
  stepsDone: number;
  stepsTotal: number;
}

export function BuildSummary({
  title,
  description,
  fileCount,
  data,
  onContinue,
}: {
  title: string;
  description: string | null;
  fileCount: number;
  data: BuildSummaryData;
  onContinue: () => void;
}) {
  const stats: Array<{ label: string; value: string }> = [
    { label: 'Build time', value: fmtDuration(data.durationS) },
    { label: 'Steps completed', value: `${data.stepsDone} of ${data.stepsTotal}` },
    ...(data.usage
      ? [
          { label: 'Tokens read', value: fmt(data.usage.inputTokens) },
          { label: 'Tokens written', value: fmt(data.usage.outputTokens) },
        ]
      : []),
    { label: 'Files created', value: String(fileCount) },
    { label: 'Version', value: '#1 — saved' },
  ];

  return (
    <div className="fixed inset-0 z-[120] flex flex-col bg-surface">
      <div className="modal-in mx-auto flex h-full w-full max-w-[880px] flex-col justify-center px-6 py-10 sm:px-10">
        <p className="text-center text-[11px] font-bold uppercase tracking-wider text-accent-deep">Your first version is ready</p>

        <div className="mt-8 grid gap-10 sm:grid-cols-2">
          {/* what was built */}
          <div>
            <h1 className="text-2xl font-extrabold tracking-tight">{title}</h1>
            {description && <p className="mt-3 text-sm leading-relaxed text-muted">{description}</p>}
            <ul className="mt-6 space-y-2 text-sm">
              <li className="flex items-center gap-2">
                <Check /> Playable right now in the preview
              </li>
              <li className="flex items-center gap-2">
                <Check /> Saved as version #1 — every change from here is undoable
              </li>
              <li className="flex items-center gap-2">
                <Check /> Sparky reviews it in the background and fixes what it finds
              </li>
            </ul>
          </div>

          {/* the run */}
          <div className="rounded-card border border-line bg-page p-5">
            <p className="text-[11px] font-bold uppercase tracking-wider text-muted">This build</p>
            <dl className="mt-3 divide-y divide-line/60">
              {stats.map(s => (
                <div key={s.label} className="flex items-baseline justify-between py-2.5">
                  <dt className="text-sm text-muted">{s.label}</dt>
                  <dd className="text-sm font-bold tabular-nums">{s.value}</dd>
                </div>
              ))}
            </dl>
          </div>
        </div>

        <button
          type="button"
          onClick={onContinue}
          autoFocus
          className="mx-auto mt-10 inline-flex h-12 w-full max-w-[360px] items-center justify-center rounded-field bg-accent text-sm font-bold text-accent-ink transition hover:brightness-110"
        >
          Open the studio →
        </button>
      </div>
    </div>
  );
}

function Check() {
  return (
    <span className="grid h-5 w-5 shrink-0 place-items-center rounded-full bg-accent/15 text-accent-deep">
      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M4 12.5l5.5 5.5L20 7" />
      </svg>
    </span>
  );
}
